/**
 * db-servers/api — panel ITE „Serwery baz".
 *
 * Rejestr serwerów PostgreSQL klientów, na których mogą leżeć bazy tenantów
 * (docs/ADR/0113b-baza-tenanta-na-serwerze-klienta.md §6).
 * Endpointy (`/api/admin/db-servers`, uprawnienie `db_servers.manage`):
 *   GET /, POST /, GET /:id, PATCH /:id, DELETE /:id,
 *   POST /:id/check (sprawdzenie połączenia), POST /:id/prepare (przygotowanie serwera),
 *   POST /:id/restore (przywrócenie usuniętego); GET /?includeDeleted=true — także usunięte;
 *   GET /install-script (skrypt instalacji serwera u klienta), ?format=info (IP aplikacji, suma).
 * Hasła nigdy nie wracają z serwera ani nie są przechowywane we froncie po użyciu.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type DbServerSslMode = 'disable' | 'require' | 'verify-full';
export type DbServerStatus = 'new' | 'active' | 'disabled';

export interface DbServerDto {
  id: string;
  name: string;
  host: string;
  port: number;
  sslMode: DbServerSslMode;
  hasCaCert: boolean;
  appUser: string;
  status: DbServerStatus;
  preparedAt: string | null;
  lastCheckAt: string | null;
  lastCheckOk: boolean | null;
  lastCheckError: string | null;
  notes: string | null;
  tenantCount: number;
  createdAt: string;
  updatedAt: string;
  /** Tylko przy `includeDeleted=true`: data usunięcia (null dla aktywnych). */
  deletedAt?: string | null;
}

export interface DbServerCheckItem {
  name: string;
  ok: boolean;
  detail?: string;
}

export interface DbServerCheckResult {
  ok: boolean;
  serverVersion?: string | null;
  sslInUse?: boolean | null;
  checks: DbServerCheckItem[];
}

export interface DbServerCreateInput {
  name: string;
  host: string;
  port?: number;
  sslMode?: DbServerSslMode;
  caCert?: string | null;
  appUser?: string;
  notes?: string | null;
}

export interface DbServerUpdateInput {
  name?: string;
  host?: string;
  port?: number;
  sslMode?: DbServerSslMode;
  caCert?: string | null;
  appUser?: string;
  notes?: string | null;
  enabled?: boolean;
}

export interface DbServerPrepareInput {
  superuserUser: string;
  superuserPassword: string;
  superuserDatabase?: string;
}

export interface DbServerResultResponse {
  result: DbServerCheckResult;
  item: DbServerDto;
}

export const DB_SERVERS_KEY = ['admin', 'db-servers'] as const;

export function useDbServers(options?: { enabled?: boolean; includeDeleted?: boolean }) {
  const includeDeleted = options?.includeDeleted ?? false;
  return useQuery<DbServerDto[]>({
    enabled: options?.enabled ?? true,
    queryKey: includeDeleted ? [...DB_SERVERS_KEY, 'with-deleted'] : DB_SERVERS_KEY,
    queryFn: async () => {
      const res = includeDeleted
        ? await api.get<{ items: DbServerDto[] }>('/admin/db-servers', {
            params: { includeDeleted: true },
          })
        : await api.get<{ items: DbServerDto[] }>('/admin/db-servers');
      return res.data.items;
    },
    retry: false,
  });
}

export const INSTALL_SCRIPT_FILE = 'install-client-db-server.sh';

export interface InstallScriptInfo {
  /** Adres(y) naszego serwera aplikacji do `--app-ip`; pusta lista = do uzupełnienia ręcznie. */
  appIps: string[];
  sha256: string;
}

export function useInstallScriptInfo(enabled: boolean) {
  return useQuery<InstallScriptInfo>({
    enabled,
    queryKey: [...DB_SERVERS_KEY, 'install-script-info'],
    queryFn: async () =>
      (
        await api.get<InstallScriptInfo>('/admin/db-servers/install-script', {
          params: { format: 'info' },
        })
      ).data,
    retry: false,
  });
}

/** Pobiera skrypt instalacyjny (żądanie z sesją admina) i oddaje przeglądarce jako plik. */
export async function downloadInstallScript(): Promise<void> {
  const res = await api.get<Blob>('/admin/db-servers/install-script', { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = INSTALL_SCRIPT_FILE;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Zwolnienie z opóźnieniem — natychmiastowe potrafi przerwać pobieranie (wzorzec AttachmentsSection).
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: DB_SERVERS_KEY });
}

export function useCreateDbServer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: DbServerCreateInput) => {
      const res = await api.post<{ item: DbServerDto }>('/admin/db-servers', input);
      return res.data.item;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateDbServer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: DbServerUpdateInput }) => {
      const res = await api.patch<{ item: DbServerDto }>(`/admin/db-servers/${id}`, input);
      return res.data.item;
    },
    onSuccess: () => invalidate(),
  });
}

export function useDeleteDbServer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/db-servers/${id}`);
      return id;
    },
    onSuccess: () => invalidate(),
  });
}

export function useRestoreDbServer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ item: DbServerDto }>(`/admin/db-servers/${id}/restore`);
      return res.data.item;
    },
    onSuccess: () => invalidate(),
  });
}

export function useCheckDbServer() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<DbServerResultResponse>(`/admin/db-servers/${id}/check`);
      return res.data;
    },
    // Wynik sprawdzenia zapisuje lastCheck* po stronie serwera — odśwież listę także po błędzie.
    onSettled: () => invalidate(),
  });
}

export function usePrepareDbServer() {
  const invalidate = useInvalidate();
  return useMutation({
    // Hasło administratora Postgresa nie może zostać w cache mutacji po zakończeniu.
    gcTime: 0,
    mutationFn: async ({ id, input }: { id: string; input: DbServerPrepareInput }) => {
      const res = await api.post<DbServerResultResponse>(`/admin/db-servers/${id}/prepare`, input);
      return res.data;
    },
    onSettled: () => invalidate(),
  });
}

/** Kod, komunikat i dane z odpowiedzi błędu API (`{ error: 'KOD', message?, ... }`). */
export function dbServerErrorInfo(err: unknown): {
  code: string | null;
  message: string | null;
  data: Record<string, unknown> | null;
} {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data ?? null;
  const code = typeof data?.error === 'string' ? data.error : null;
  const message = typeof data?.message === 'string' ? data.message : null;
  return { code, message, data };
}
